import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import QuestionCard from './QuestionCard';

/**
 * SAFE MODE: Region Selection Step
 * - Replaces City Selection in NORMAL booking flow
 * - 5 regions only (Eastern, Central, Western, Northern, Southern)
 * - Used for discovery/UX only, NOT for hard eligibility rules
 * - Photographer matching still uses existing service range logic
 */
const RegionSelectionStep = ({ formData, onUpdate, isDirectMode }) => {
  const [selectedRegion, setSelectedRegion] = useState(formData?.region || '');

  // Regions with their Arabic labels and descriptions
  const regions = [
    { 
      value: 'eastern', 
      label: 'الشرقية', 
      description: 'الدمام، الخبر، الظهران، الجبيل',
      icon: 'Sunrise'
    },
    { 
      value: 'central', 
      label: 'الوسطى', 
      description: 'الرياض، القصيم، حائل',
      icon: 'Building2'
    },
    { 
      value: 'western', 
      label: 'الغربية', 
      description: 'جدة، مكة، المدينة، الطائف',
      icon: 'Palmtree'
    },
    { 
      value: 'northern', 
      label: 'الشمالية', 
      description: 'تبوك، الجوف، عرعر',
      icon: 'Mountain'
    },
    { 
      value: 'southern', 
      label: 'الجنوبية', 
      description: 'أبها، نجران، جازان',
      icon: 'Trees'
    }
  ];

  // Sync with formData on mount
  useEffect(() => {
    if (formData?.region && formData.region !== selectedRegion) {
      setSelectedRegion(formData.region);
    }
  }, [formData?.region]);

  const handleRegionChange = (value, label) => {
    setSelectedRegion(value);
    onUpdate({ 
      region: value,
      regionLabel: label
    });
  };

  // In direct mode, this step is skipped entirely
  if (isDirectMode) {
    return null;
  }

  return (
    <QuestionCard
      icon="Map"
      title="في أي منطقة تحتاج جلسة التصوير؟"
      description="اختر المنطقة للبدء. سيتم تحديد موقعك الدقيق في الخطوة التالية."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {regions.map((region) => (
          <button
            key={region.value}
            onClick={() => handleRegionChange(region.value, region.label)}
            className={`p-4 rounded-xl border-2 text-right transition-all hover:border-primary hover:bg-primary/5 ${
              selectedRegion === region.value
                ? 'border-primary bg-primary/10 shadow-sm'
                : 'border-border bg-background'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                selectedRegion === region.value
                  ? 'bg-primary/20'
                  : 'bg-slate-100'
              }`}>
                <Icon 
                  name={region.icon} 
                  size={20} 
                  className={selectedRegion === region.value ? 'text-primary' : 'text-slate-500'} 
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-base font-semibold text-foreground">
                    {region.label}
                  </span>
                  {selectedRegion === region.value && (
                    <Icon name="CheckCircle2" size={20} color="var(--color-primary)" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {region.description}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
      
      <p className="text-xs text-muted-foreground mt-4 text-center">
        💡 سيتم التحقق من توفر المصورين بناءً على موقعك الدقيق
      </p>
    </QuestionCard>
  );
};

export default RegionSelectionStep;
